import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Copy, Eye, EyeOff, ToggleLeft, ToggleRight, Clock, Users, RefreshCw, Link, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BRAND_COLORS = {
  jet:          'bg-orange-100 text-orange-800 border-orange-300',
  shell:        'bg-yellow-100 text-yellow-800 border-yellow-300',
  aral:         'bg-blue-100 text-blue-800 border-blue-300',
  esso:         'bg-red-100 text-red-800 border-red-300',
  ran:          'bg-rose-100 text-rose-800 border-rose-300',
  hem:          'bg-green-100 text-green-800 border-green-300',
  avia:         'bg-red-100 text-red-900 border-red-400',
  totalenergies:'bg-orange-50 text-orange-700 border-orange-200',
  omv:          'bg-blue-100 text-blue-900 border-blue-400',
  star:         'bg-rose-50 text-rose-700 border-rose-200',
  westfalen:    'bg-blue-200 text-blue-900 border-blue-500',
  agip:         'bg-yellow-100 text-red-700 border-yellow-300',
  q1:           'bg-red-50 text-red-700 border-red-200',
  raiffeisen:   'bg-blue-50 text-blue-700 border-yellow-300',
  bft:          'bg-blue-100 text-blue-800 border-blue-200',
  sprint:       'bg-orange-100 text-orange-700 border-blue-200',
};

const ALL_BRANDS = [
  'jet', 'shell', 'aral', 'esso', 'totalenergies', 'omv',
  'star', 'westfalen', 'agip', 'q1', 'raiffeisen', 'bft', 'sprint',
  'ran', 'hem', 'avia',
];

export function DemoSessions() {
  const [tokens, setTokens] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDesc, setNewDesc] = useState('');
  const [baseUrl] = useState(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return backendUrl.replace('/api', '').replace(':8001', '').replace('api.', 'app.') || 'https://app.allflowx.de';
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [tokRes, sesRes] = await Promise.all([
        axios.get(`${API}/api/superadmin/demo-tokens`, { headers }),
        axios.get(`${API}/api/superadmin/demo-sessions`, { headers }),
      ]);
      setTokens(Array.isArray(tokRes.data) ? tokRes.data : []);
      setSessions(Array.isArray(sesRes.data) ? sesRes.data : []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Zugriff verweigert – bitte erneut einloggen (Rolle: Superadmin erforderlich)');
      } else {
        toast.error('Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const createToken = async () => {
    try {
      await axios.post(`${API}/api/superadmin/demo-tokens`, { description: newDesc || 'Demo-Link' }, { headers });
      setNewDesc('');
      toast.success('Token erstellt');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (err.response?.status === 403) {
        toast.error('Zugriff verweigert – bitte erneut einloggen');
      } else {
        toast.error(`Fehler: ${detail || err.message}`);
      }
    }
  };

  const toggleToken = async (id) => {
    try {
      await axios.patch(`${API}/api/superadmin/demo-tokens/${id}/toggle`, {}, { headers });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Umschalten');
    }
  };

  const deleteToken = async (id) => {
    if (!window.confirm('Token löschen?')) return;
    try {
      await axios.delete(`${API}/api/superadmin/demo-tokens/${id}`, { headers });
      toast.success('Gelöscht');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Session und alle Daten löschen?')) return;
    try {
      const res = await axios.delete(`${API}/api/superadmin/demo-sessions/${id}`, { headers });
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error('Fehler'); }
  };

  const copyLink = (token, brand = 'jet') => {
    const url = `${baseUrl}/demo/${brand}?token=${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Link kopiert!');
  };

  const minutesLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    return Math.max(0, Math.floor(diff / 60000));
  };

  const [purging, setPurging] = useState(false);

  const purgeAllDemoData = async () => {
    if (!window.confirm(
      '⚠️ ALLE Demo-Daten aus der Datenbank löschen?\n\n' +
      'Dies löscht alle is_demo=true Dokumente aus ALLEN Collections:\n' +
      'Kunden, Standorte, Nutzer, Aufgaben, Backshop, etc.\n\n' +
      'Aktive Demo-Sessions werden dabei ebenfalls beendet.\n\n' +
      'Diese Aktion kann nicht rückgängig gemacht werden!'
    )) return;

    setPurging(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/demo/purge-all`, {}, { headers });
      toast.success(`Bereinigt: ${res.data.total_deleted} Datensätze gelöscht`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Bereinigen');
    } finally {
      setPurging(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Demo-System</h1>
            <p className="text-sm text-gray-500 mt-1">Sandbox-Links für Bezirksleiter verwalten</p>
          </div>
          <Button variant="outline" onClick={fetchData} size="sm"><RefreshCw className="h-4 w-4" /></Button>
        </div>

        {/* Demo Tokens */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Link className="h-4 w-4 text-blue-500" /> Demo-Links (Tokens)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Create */}
            <div className="flex gap-2">
              <Input placeholder="Beschreibung (z.B. JET Herbstkampagne)" value={newDesc}
                onChange={e => setNewDesc(e.target.value)} onKeyDown={e => e.key === 'Enter' && createToken()} />
              <Button onClick={createToken} data-testid="create-demo-token-btn">
                <Plus className="h-4 w-4 mr-1" /> Erstellen
              </Button>
            </div>

            {/* Token List */}
            {tokens.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Noch keine Tokens. Erstelle deinen ersten Demo-Link.</p>
            ) : (
              <div className="space-y-2">
                {tokens.map(t => (
                  <div key={t.id} className={`p-3 rounded-lg border space-y-2.5 ${
                    t.is_active
                      ? 'bg-green-50 dark:bg-green-900/10 border-green-200'
                      : 'bg-gray-50 dark:bg-gray-800 border-gray-200 opacity-60'
                  }`}>

                    {/* Zeile 1: Status + Beschreibung + Nutzung + Aktions-Icons */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                          t.is_active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {t.is_active ? 'Aktiv' : 'Deaktiviert'}
                        </span>
                        <span className="text-sm font-medium truncate">{t.description}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0">({t.usage_count || 0}x)</span>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <button onClick={() => toggleToken(t.id)}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          title="Aktivieren/Deaktivieren"
                          data-testid={`toggle-token-${t.id}`}>
                          {t.is_active
                            ? <ToggleRight className="h-4 w-4 text-green-600" />
                            : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                        </button>
                        <button onClick={() => deleteToken(t.id)}
                          className="p-1.5 hover:bg-red-50 rounded text-red-500"
                          data-testid={`delete-token-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Zeile 2: Token-Hash */}
                    <div className="text-xs text-gray-400 font-mono truncate">{t.token}</div>

                    {/* Zeile 3: Brand-Buttons – wrappen auf allen Bildschirmgrößen */}
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_BRANDS.map(b => (
                        <button key={b} onClick={() => copyLink(t.token, b)}
                          className={`text-xs px-2 py-1 rounded border ${BRAND_COLORS[b]} hover:opacity-70 active:scale-95 font-medium transition-all`}
                          title={`${b.toUpperCase()}-Demo-Link kopieren`}
                          data-testid={`copy-${b}-link`}>
                          {b.toUpperCase()}
                        </button>
                      ))}
                    </div>

                  </div>
                ))}
              </div>
            )}

            {/* URL-Format – responsive mit Zeilenumbruch */}
            <div className="text-xs text-gray-500 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg space-y-1">
              <strong>URL-Format:</strong>
              <div className="font-mono break-all text-gray-400 leading-relaxed">
                {baseUrl}/demo/<span className="text-blue-600 dark:text-blue-400">[marke]</span>?token=<span className="text-blue-600 dark:text-blue-400">[token]</span>
              </div>
              <div className="text-gray-400 break-all">
                Marken: jet · shell · aral · esso · totalenergies · omv · star · westfalen · agip · q1 · raiffeisen · bft · sprint · ran · hem · avia
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Sessions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-orange-500" />
              Aktive Demo-Sessions ({sessions.filter(s => !s.cleaned_up).length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.filter(s => !s.cleaned_up).length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Keine aktiven Demo-Sessions.</p>
            ) : (
              <div className="space-y-2">
                {sessions.filter(s => !s.cleaned_up).map(s => (
                  <div key={s.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full border font-bold ${BRAND_COLORS[s.brand] || 'bg-gray-100'}`}>
                        {s.brand?.toUpperCase()}
                      </span>
                      <div>
                        <div className="text-sm font-medium">{s.customer_id}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Gestartet: {new Date(s.created_at).toLocaleString('de-DE')}
                          <span className="ml-2 text-orange-600 font-medium">
                            {minutesLeft(s.expires_at)}min verbleibend
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button variant="destructive" size="sm" onClick={() => deleteSession(s.id)}
                      data-testid={`delete-session-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Löschen
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        {/* Gefahr-Zone: Alles bereinigen */}
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4" /> Datenbank-Cleanup (Produktion)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Löscht <strong>alle Demo-Datensätze</strong> (<code className="bg-gray-100 px-1 rounded text-xs">is_demo: true</code>) aus
              allen Collections (Kunden, Standorte, Nutzer, Aufgaben, Backshop, etc.).
              Nutze diesen Button um die Produktions-Datenbank nach Tests zu bereinigen.
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={purgeAllDemoData}
                disabled={purging}
                data-testid="purge-all-demo-btn"
                className="gap-1.5"
              >
                {purging
                  ? <><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Bereinige…</>
                  : <><Trash2 className="h-3.5 w-3.5" /> Alle Demo-Daten löschen</>
                }
              </Button>
              <span className="text-xs text-gray-400">Erzeugt keine Wiederherstellungspunkte – bitte vorher sichern.</span>
            </div>
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
